export interface PracticalInfo {
  wifi: {
    network: string;
    password: string;
  };
  socialMedia: {
    hashtag: string;
  };
  location: {
    name: string;
    mapLink: string;
    mapEmbed: string;
    parkingLink: string;
  };
  postEvent: {
    name: string;
    time: string;
    mapLink: string;
    mapEmbed: string;
  };
}

// Datos por defecto (fallback si falla la carga)
export const defaultPracticalInfo: PracticalInfo = {
  wifi: {
    network: "VIVEROWIRELESS",
    password: "Cein2007",
  },
  socialMedia: {
    hashtag: "#openJAKALA2026",
  },
  location: {
    name: "CEIN, Noain",
    mapLink: "https://maps.app.goo.gl/EZkNuGVbLNQz3kvw7",
    mapEmbed:
      "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2933!2d-1.6368795!3d42.7542658!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd50910aaaada8fd%3A0xb7d6b9f297962622!2sCEIN!5e0!3m2!1ses!2ses",
    parkingLink: "https://maps.app.goo.gl/6DsSh6vEXHVoqqZ48",
  },
  postEvent: {
    name: "The Beer Garden - Terraza Chill Out & Cócteles",
    time: "18:00",
    mapLink: "https://maps.app.goo.gl/fruA72swtAD7UgFE6",
    mapEmbed:
      "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3468.100909060465!2d-1.6677972233428646!3d42.81107677115468!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd509333d70e464b%3A0x6b28921fab85460c!2sThe%20Beer%20Garden%20-%20Terraza%20Chill%20Out%20%26%20C%C3%B3cteles!5e1!3m2!1ses!2ses!4v1789459050271!5m2!1ses!2ses",
  },
};

// ID de tu Google Sheet (el mismo que para programación o uno diferente)
const SHEET_ID = import.meta.env.VITE_SHEET_ID || "";
const SHEET_NAME = "InfoPractica"; // Nombre de la hoja

/**
 * Obtiene los datos de información práctica desde Google Sheets
 * Si falla, devuelve los datos por defecto
 */
export async function fetchPracticalInfo(): Promise<PracticalInfo> {
  // Si no hay SHEET_ID configurado, usar datos por defecto
  if (!SHEET_ID) {
    console.warn("SHEET_ID no configurado, usando datos por defecto");
    return defaultPracticalInfo;
  }

  try {
    // URL pública de Google Sheets como JSON
    const url = `https://docs.google.com/spreadsheets/d/${SHEET_ID}/gviz/tq?tqx=out:json&sheet=${SHEET_NAME}`;

    const response = await fetch(url);
    const text = await response.text();

    // Google devuelve JSONP, necesitamos extraer el JSON
    const json = JSON.parse(text.substring(47).slice(0, -2));

    // Parsear los datos
    const rows = json.table.rows;
    const practicalInfo = parseSheetRows(rows);

    return practicalInfo || defaultPracticalInfo;
  } catch (error) {
    console.error("Error al cargar datos desde Google Sheets:", error);
    return defaultPracticalInfo;
  }
}

/**
 * Parsea las filas de la hoja de Google
 * Formato esperado: key | value
 *
 * Filas esperadas:
 * wifi_network | VIVEROWIRELESS
 * wifi_password | Cein2007
 * hashtag | #OpenSpaceJakala2025
 */
function parseSheetRows(rows: any[]): PracticalInfo | null {
  const data: { [key: string]: string } = {};

  // Saltar la primera fila (headers)
  for (let i = 1; i < rows.length; i++) {
    const row = rows[i];
    if (!row.c || !row.c[0]?.v) continue; // Saltar filas vacías

    const key = row.c[0]?.v || "";
    const value = row.c[1]?.v || "";

    if (key && value) {
      data[key] = value;
    }
  }

  // Si no hay datos, devolver null
  if (Object.keys(data).length === 0) {
    return null;
  }

  // Construir el objeto con los datos parseados o usar los por defecto
  return {
    wifi: {
      network: data.wifi_network || defaultPracticalInfo.wifi.network,
      password: data.wifi_password || defaultPracticalInfo.wifi.password,
    },
    socialMedia: {
      hashtag: data.hashtag || defaultPracticalInfo.socialMedia.hashtag,
    },
    location: {
      name: data.location_name || defaultPracticalInfo.location.name,
      mapLink: data.location_map || defaultPracticalInfo.location.mapLink,
      mapEmbed: data.location_embed || defaultPracticalInfo.location.mapEmbed,
      parkingLink: data.parking_map || defaultPracticalInfo.location.parkingLink,
    },
    postEvent: {
      name: data.post_event_name || defaultPracticalInfo.postEvent.name,
      time: data.post_event_time || defaultPracticalInfo.postEvent.time,
      mapLink: data.post_event_map || defaultPracticalInfo.postEvent.mapLink,
      mapEmbed:
        data.post_event_embed || defaultPracticalInfo.postEvent.mapEmbed,
    },
  };
}
